import {
    Component,
    DestroyRef,
    HostListener,
    inject,
    OnDestroy,
    OnInit,
    Signal,
    ChangeDetectionStrategy
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LanguageCode } from '@gbr-monorepo/interfaces';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngxs/store';
import { filter, map, merge, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FooterComponent as SharedFooterComponent, HeaderComponent as SharedHeaderComponent } from 'shared-ui';
import { SetUserLanguage } from './auth/state/user.action';
import { UserState } from './auth/state/user.state';
import { ThemeService } from './shared/services/theme.service';
import { ScreenTooSmallComponent } from './shared/components/screen-too-small/screen-too-small.component';
import { SkipLinkComponent } from './shared/components/skip-link/skip-link.component';
import { HideScreenTooSmall, ShowScreenTooSmall } from './shared/state/screen.actions';
import { ScreenState } from './shared/state/screen.state';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ScreenTooSmallComponent,
        SharedHeaderComponent,
        RouterOutlet,
        SharedFooterComponent,
        TranslateModule,
        SkipLinkComponent
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    private store = inject(Store);
    private translate = inject(TranslateService);
    private theme = inject(ThemeService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private titleService = inject(Title);
    private readonly destroyRef = inject(DestroyRef);

    protected isScreenTooSmall: Signal<boolean> = select(ScreenState.isScreenTooSmall);
    protected readonly selectedLang = select(UserState.userLanguage);
    protected readonly user = select(UserState.user);
    protected readonly picture = select(UserState.profilePicture);
    protected readonly allowProfilePicture = select(UserState.allowProfilePicture);
    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.routeToRedirect();
        this.translate.setDefaultLang('de');
        this.translate.use(this.selectedLang());
        this.theme.setTheme('light');
        this.onResize();
        this.translatePageTitle();
    }

    public changeLanguage(language: LanguageCode): void {
        this.store.dispatch(new SetUserLanguage({ language }));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private routeToRedirect(): void {
        const url = window.localStorage.getItem('redirectUrl');
        if (url) {
            void this.router.navigateByUrl(url);
            window.localStorage.removeItem('redirectUrl');
        }
    }

    @HostListener('window:resize')
    onResize(): void {
        const requiredWindowWidth = this.store.selectSnapshot(ScreenState.requiredScreenWidth);

        const effectiveWidth = window.innerWidth;
        this.store.dispatch(effectiveWidth < requiredWindowWidth ? new ShowScreenTooSmall() : new HideScreenTooSmall());
    }

    private translatePageTitle(): void {
        merge(this.router.events.pipe(filter(event => event instanceof NavigationEnd)), this.translate.onLangChange)
            .pipe(
                map(() => this.deepestRouteWithTitle()),
                map(route => route.snapshot.data['title'] || 'page-titles.home'),
                switchMap(title => this.translate.get(title)),
                takeUntil(this.destroy$),
                tap(translatedTitle => this.titleService.setTitle(translatedTitle))
            )
            .subscribe();
    }

    private deepestRouteWithTitle(): ActivatedRoute {
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }
}
